/** Simulated sharing model for Send It Safely. Not a copy of any real product. */

export type Role = "view" | "comment" | "edit";
export type Audience = "person" | "group" | "anyone";

export interface Person {
  id: string;
  name: string;
  email: string;
  org: string;
  orgLabel: string;
  trusted: boolean;
  note: string;
}

export interface Group {
  id: string;
  name: string;
  memberIds: string[];
}

export interface SimFile {
  id: string;
  name: string;
  kind: "guide" | "working" | "external" | "schedule" | "flyer";
  summary: string;
  preview: string[];
  publicApproved: boolean;
}

export interface Grant {
  id: string;
  fileId: string;
  who: "person" | "group";
  whoId: string;
  role: Role;
  via: "direct" | "parent" | "guest";
}

export interface ShareLink {
  id: string;
  fileId: string;
  audience: "anyone" | "named";
  personId?: string;
  role: Role;
  enabled: boolean;
  /** review links stop working when the clock moves to "done". */
  expiresAtDone: boolean;
}

export interface AppGrant {
  id: string;
  appId: string;
  scope: "selected-doc" | "broad";
  revoked: boolean;
}

export interface World {
  grants: Grant[];
  links: ShareLink[];
  /** Guest membership of the project, separate from a direct file grant. */
  guestIds: string[];
  filePlace: Record<string, "shared-folder" | "project">;
  apps: AppGrant[];
  clock: "review" | "done";
  reported: string[];
}

const RANK: Record<Role, number> = { view: 1, comment: 2, edit: 3 };

export const PEOPLE: Person[] = [
  {
    id: "morgan-internal",
    name: "Morgan Lee",
    email: "morgan.lee@northstar.example",
    org: "northstar",
    orgLabel: "Northstar (your organisation)",
    trusted: true,
    note: "Trusted directory. This is the colleague on the workshop team.",
  },
  {
    id: "morgan-external",
    name: "Morgan Lee",
    email: "morgan.lee@brightlane.example",
    org: "brightlane",
    orgLabel: "Brightlane (external)",
    trusted: false,
    note: "Same name. Different organisation. Not the colleague in the request.",
  },
  {
    id: "alex",
    name: "Alex Kim",
    email: "alex.kim@clearpath.example",
    org: "clearpath",
    orgLabel: "Clearpath (approved facilitator)",
    trusted: true,
    note: "Trusted directory. Approved external facilitator until the workshop ends.",
  },
  {
    id: "riley",
    name: "Riley Chen",
    email: "riley.chen@northstar.example",
    org: "northstar",
    orgLabel: "Northstar (your organisation)",
    trusted: true,
    note: "Workshop project team.",
  },
  {
    id: "former",
    name: "Jordan Hale",
    email: "jordan.hale@oldpartner.example",
    org: "oldpartner",
    orgLabel: "Old Partner (former collaborator)",
    trusted: false,
    note: "No longer on this project. Still listed because a folder shared with them earlier.",
  },
];

export const GROUPS: Group[] = [
  { id: "project-team", name: "Workshop project team", memberIds: ["morgan-internal", "riley"] },
];

export const FILES: SimFile[] = [
  {
    id: "arrival",
    name: "Workshop arrival guide",
    kind: "guide",
    summary: "For Morgan only. They only need to read it.",
    preview: [
      "Arrive at the north entrance by 09:00.",
      "The workshop room is Birch, level 2.",
      "This version is for Morgan Lee at Northstar only.",
    ],
    publicApproved: false,
  },
  {
    id: "working",
    name: "Workshop guide (internal working copy)",
    kind: "working",
    summary: "Internal comments and personal contact details. Not approved for an outside reviewer.",
    preview: [
      "Internal note: do not send this copy outside Northstar.",
      "Contact: Priya Shah, priya.shah@northstar.example, +1 555 0100.",
      "Draft comment: budget still being argued.",
    ],
    publicApproved: false,
  },
  {
    id: "external",
    name: "Workshop guide (approved external review)",
    kind: "external",
    summary: "Already prepared for Alex. No internal comments or personal phone numbers.",
    preview: [
      "Approved for external review by Alex Kim at Clearpath.",
      "Session outline and timings only.",
      "No internal comments on this copy.",
    ],
    publicApproved: false,
  },
  {
    id: "schedule",
    name: "Workshop schedule",
    kind: "schedule",
    summary: "The project team needs to edit this together.",
    preview: [
      "Monday 09:30 welcome.",
      "Monday 11:00 group exercise.",
      "Tuesday 15:00 close.",
    ],
    publicApproved: false,
  },
  {
    id: "flyer",
    name: "Workshop flyer",
    kind: "flyer",
    summary: "Approved for public distribution. No confidential information.",
    preview: [
      "Marked: approved for public distribution.",
      "Workplace workshop, open sessions, no internal details.",
      "Anyone with the link may read it. They should not be able to edit it.",
    ],
    publicApproved: true,
  },
];

let seq = 1;
const nid = (p: string) => `${p}-${seq++}`;

export function initialWorld(): World {
  seq = 1;
  return {
    grants: [
      { id: "g-parent-former", fileId: "schedule", who: "person", whoId: "former", role: "view", via: "parent" },
    ],
    links: [],
    guestIds: [],
    filePlace: { schedule: "shared-folder" },
    apps: [],
    clock: "review",
    reported: [],
  };
}

function best(roles: Role[]): Role | null {
  let top: Role | null = null;
  for (const r of roles) {
    if (!top || RANK[r] > RANK[top]) top = r;
  }
  return top;
}

export interface AccessPath {
  personId: string;
  role: Role;
  via: string;
}

/** Everyone who can open the file, and how. A narrow new grant does not cancel a wider one. */
export function effectiveAccess(world: World, fileId: string): AccessPath[] {
  const paths: AccessPath[] = [];
  const place = world.filePlace[fileId] ?? "project";
  for (const g of world.grants) {
    if (g.fileId !== fileId) continue;
    if (g.via === "parent" && place !== "shared-folder") continue;
    if (g.who === "person") paths.push({ personId: g.whoId, role: g.role, via: g.via });
    else {
      const group = GROUPS.find((x) => x.id === g.whoId);
      for (const id of group?.memberIds ?? []) paths.push({ personId: id, role: g.role, via: `group:${g.whoId}` });
    }
  }
  if (place === "project") {
    for (const id of world.guestIds) paths.push({ personId: id, role: "comment", via: "guest" });
  }
  for (const link of world.links) {
    if (link.fileId !== fileId || !link.enabled) continue;
    if (link.expiresAtDone && world.clock === "done") continue;
    if (link.audience === "anyone") paths.push({ personId: "link-holder", role: link.role, via: "anyone-link" });
    else if (link.personId) paths.push({ personId: link.personId, role: link.role, via: "named-link" });
  }
  const byPerson = new Map<string, AccessPath>();
  for (const p of paths) {
    const prev = byPerson.get(p.personId);
    if (!prev || RANK[p.role] > RANK[prev.role]) byPerson.set(p.personId, p);
    else if (prev && p.via !== prev.via && RANK[p.role] === RANK[prev.role]) {
      byPerson.set(p.personId, { ...prev, via: `${prev.via} + ${p.via}` });
    }
  }
  return [...byPerson.values()];
}

export interface AccessRow {
  key: string;
  kind: "direct" | "parent" | "group" | "link" | "named-link" | "guest";
  role: Role;
  personId?: string;
  groupId?: string;
  /** Id passed to removeGrant, removeLink, or removeGuest. Absent for inherited folder access. */
  removeId?: string;
  expired?: boolean;
}

/** One row per route into a file, for the Manage access window. */
export function accessRows(world: World, fileId: string): AccessRow[] {
  const rows: AccessRow[] = [];
  const place = world.filePlace[fileId] ?? "project";
  for (const g of world.grants) {
    if (g.fileId !== fileId) continue;
    if (g.via === "parent" && place !== "shared-folder") continue;
    if (g.via === "guest") continue;
    rows.push({
      key: g.id,
      kind: g.via === "parent" ? "parent" : g.who === "group" ? "group" : "direct",
      role: g.role,
      personId: g.who === "person" ? g.whoId : undefined,
      groupId: g.who === "group" ? g.whoId : undefined,
      removeId: g.via === "parent" ? undefined : g.id,
    });
  }
  for (const link of world.links) {
    if (link.fileId !== fileId) continue;
    const expired = !link.enabled || (link.expiresAtDone && world.clock === "done");
    if (!link.enabled && !expired) continue;
    rows.push({
      key: link.id,
      kind: link.audience === "anyone" ? "link" : "named-link",
      role: link.role,
      personId: link.personId,
      removeId: link.id,
      expired,
    });
  }
  if (place === "project") {
    for (const id of world.guestIds) {
      rows.push({
        key: `guest-${fileId}-${id}`,
        kind: "guest",
        role: "comment",
        personId: id,
        removeId: id,
      });
    }
  }
  return rows;
}

export function removeGrant(world: World, id: string): World {
  return { ...world, grants: world.grants.filter((g) => g.id !== id) };
}

export function removeLink(world: World, id: string): World {
  return { ...world, links: world.links.filter((l) => l.id !== id) };
}

export function removeGuest(world: World, personId: string): World {
  return { ...world, guestIds: world.guestIds.filter((id) => id !== personId) };
}

export function roleOf(world: World, fileId: string, personId: string): Role | null {
  const hits = effectiveAccess(world, fileId).filter((p) => p.personId === personId);
  return best(hits.map((h) => h.role));
}

export function hasAnyoneLink(world: World, fileId: string, role?: Role): boolean {
  return world.links.some(
    (l) =>
      l.fileId === fileId &&
      l.enabled &&
      l.audience === "anyone" &&
      !(l.expiresAtDone && world.clock === "done") &&
      (role ? l.role === role : true)
  );
}

export interface ShareDraft {
  fileId: string;
  audience: Audience;
  personId?: string;
  groupId?: string;
  role: Role;
  expiresAtDone?: boolean;
  asGuest?: boolean;
}

export function applyShare(world: World, draft: ShareDraft): World {
  const next: World = {
    ...world,
    grants: [...world.grants],
    links: [...world.links],
    guestIds: [...world.guestIds],
  };
  if (draft.audience === "anyone") {
    next.links.push({
      id: nid("link"),
      fileId: draft.fileId,
      audience: "anyone",
      role: draft.role,
      enabled: true,
      expiresAtDone: !!draft.expiresAtDone,
    });
    return next;
  }
  if (draft.audience === "group" && draft.groupId) {
    next.grants.push({
      id: nid("grant"),
      fileId: draft.fileId,
      who: "group",
      whoId: draft.groupId,
      role: draft.role,
      via: "direct",
    });
    return next;
  }
  if (draft.personId) {
    next.grants.push({
      id: nid("grant"),
      fileId: draft.fileId,
      who: "person",
      whoId: draft.personId,
      role: draft.role,
      via: "direct",
    });
    if (draft.asGuest && !next.guestIds.includes(draft.personId)) next.guestIds.push(draft.personId);
    if (draft.expiresAtDone) {
      next.links.push({
        id: nid("link"),
        fileId: draft.fileId,
        audience: "named",
        personId: draft.personId,
        role: draft.role,
        enabled: true,
        expiresAtDone: true,
      });
    }
  }
  return next;
}

/** Drop direct shares on one file so a failed attempt can be chosen again. Inherited folder access stays. */
export function clearDirectShares(world: World, fileId: string): World {
  return {
    ...world,
    grants: world.grants.filter((g) => g.fileId !== fileId || g.via === "parent"),
    links: world.links.filter((l) => l.fileId !== fileId),
  };
}

export function moveScheduleToProject(world: World): World {
  return {
    ...world,
    filePlace: { ...world.filePlace, schedule: "project" },
    grants: world.grants.filter((g) => !(g.fileId === "schedule" && g.via === "parent")),
  };
}

export function removePersonAccess(world: World, personId: string): World {
  return {
    ...world,
    grants: world.grants.filter((g) => !(g.who === "person" && g.whoId === personId)),
    links: world.links.map((l) => (l.personId === personId ? { ...l, enabled: false } : l)),
    guestIds: world.guestIds.filter((id) => id !== personId),
  };
}

export function approveApp(world: World, appId: string, scope: AppGrant["scope"]): World {
  return { ...world, apps: [...world.apps, { id: nid("app"), appId, scope, revoked: false }] };
}

export function revokeApp(world: World, appId: string): World {
  return { ...world, apps: world.apps.map((a) => (a.appId === appId ? { ...a, revoked: true } : a)) };
}

export function reportApp(world: World, appId: string): World {
  return { ...world, reported: world.reported.includes(appId) ? world.reported : [...world.reported, appId] };
}

export interface Grade {
  ok: boolean;
  happened: string;
  why: string;
  next: string;
}

export function gradeMission(id: string, world: World): Grade {
  if (id === "m1") {
    const internal = roleOf(world, "arrival", "morgan-internal");
    const external = roleOf(world, "arrival", "morgan-external");
    const pub = hasAnyoneLink(world, "arrival");
    if (external) {
      return {
        ok: false,
        happened: "This invitation went to the external Morgan Lee.",
        why: "The name matched, but the address is at Brightlane, not Northstar.",
        next: "Open the directory and choose morgan.lee@northstar.example with View.",
      };
    }
    if (pub || internal === "edit" || internal === "comment") {
      return {
        ok: false,
        happened: internal ? `Morgan at Northstar can ${internal} the guide.` : "The guide is open more widely than the request.",
        why: "Morgan only needs to read this version. Extra access is more than the task asked for.",
        next: "Share only with the Northstar Morgan, and only with View.",
      };
    }
    if (internal === "view") {
      return {
        ok: true,
        happened: "The Northstar Morgan Lee can read the arrival guide and cannot change it.",
        why: "You matched the person by address and organisation, not by name alone.",
        next: "Continue to the next request.",
      };
    }
    return {
      ok: false,
      happened: "Morgan at Northstar still cannot open the guide.",
      why: "The request is to send this version to that one colleague.",
      next: "Choose morgan.lee@northstar.example and View, then share.",
    };
  }
  if (id === "m4") {
    const alexExt = roleOf(world, "external", "alex");
    const alexWork = roleOf(world, "working", "alex");
    const pub = hasAnyoneLink(world, "external") || hasAnyoneLink(world, "working");
    if (alexWork) {
      return {
        ok: false,
        happened: "Alex can open the internal working copy.",
        why: "That copy has internal comments and a personal phone number. The approved external version is the one prepared for Alex.",
        next: "Share the approved external review guide, not the working copy.",
      };
    }
    if (pub) {
      return {
        ok: false,
        happened: "A public link was created.",
        why: "A failed invitation is not fixed by making the file public. Alex has a verified address.",
        next: "Invite alex.kim@clearpath.example with Comment, until the workshop ends.",
      };
    }
    if (alexExt === "edit") {
      return {
        ok: false,
        happened: "Alex can edit the guide.",
        why: "The request is to suggest changes, which is Comment, not Edit.",
        next: "Set Alex to Comment on the approved external review guide.",
      };
    }
    if (alexExt === "comment" || alexExt === "view") {
      if (alexExt === "view") {
        return {
          ok: false,
          happened: "Alex can read the guide but cannot suggest changes.",
          why: "Comment is what lets a reviewer leave feedback without rewriting the file.",
          next: "Change Alex to Comment.",
        };
      }
      const expiring = world.links.some((l) => l.personId === "alex" && l.expiresAtDone) || world.guestIds.includes("alex");
      if (!expiring) {
        return {
          ok: false,
          happened: "Alex can comment, with no end date.",
          why: "Access was requested until the workshop ends.",
          next: "Turn on access until the workshop ends, or add Alex as a guest you can remove later.",
        };
      }
      return {
        ok: true,
        happened: "Alex Kim at Clearpath can comment on the approved external guide until the workshop ends.",
        why: "You used the prepared copy and the verified address, and you did not send the internal working file.",
        next: "Continue to the schedule task.",
      };
    }
    return {
      ok: false,
      happened: "Alex still cannot open a guide.",
      why: "The facilitator needs the approved external version.",
      next: "Invite alex.kim@clearpath.example with Comment.",
    };
  }
  if (id === "m5") {
    const morgan = roleOf(world, "schedule", "morgan-internal");
    const riley = roleOf(world, "schedule", "riley");
    const former = roleOf(world, "schedule", "former");
    if (former) {
      return {
        ok: false,
        happened: "Jordan Hale, the former collaborator, can still open the schedule.",
        why: "A new link for Morgan and Riley does not remove access that comes through the parent folder.",
        next: "Jordan Hale still has the schedule because the file is in the shared folder. Press Request a restricted project location, then give the Workshop project team Edit.",
      };
    }
    if (morgan === "edit" && riley === "edit") {
      return {
        ok: true,
        happened: "Morgan and Riley can edit the schedule. The former collaborator cannot open it.",
        why: "The team can do the work, and the older folder route is gone.",
        next: "Continue. One access cleanup is still left.",
      };
    }
    return {
      ok: false,
      happened: "The project team cannot both edit the schedule yet.",
      why: "View or Comment is not enough when the task is to update the schedule together.",
      next: "Give the Workshop project team Edit. That is Morgan Lee at Northstar and Riley Chen.",
    };
  }
  if (id === "m2") {
    const anyoneEdit = world.links.some((l) => l.fileId === "flyer" && l.audience === "anyone" && l.enabled && l.role === "edit");
    const anyoneView = hasAnyoneLink(world, "flyer", "view") || hasAnyoneLink(world, "flyer", "comment");
    if (anyoneEdit) {
      return {
        ok: false,
        happened: "Anyone with the link can edit the flyer.",
        why: "The public request is to read it, not change it.",
        next: "Create an anyone-with-the-link share set to View.",
      };
    }
    if (anyoneView && hasAnyoneLink(world, "flyer")) {
      const view = world.links.some((l) => l.fileId === "flyer" && l.audience === "anyone" && l.enabled && l.role === "view");
      if (!view) {
        return {
          ok: false,
          happened: "The public link allows more than reading.",
          why: "Anyone who receives the link should be able to read it, not edit it.",
          next: "Set the anyone link to View.",
        };
      }
      return {
        ok: true,
        happened: "Anyone who receives the link can read the flyer without signing in, and cannot edit it.",
        why: "This flyer is approved for public distribution. A link that still requires access would block the audience.",
        next: "Continue to the app requests.",
      };
    }
    return {
      ok: false,
      happened: "The flyer still requires an invitation.",
      why: "This flyer is approved for public distribution. Your current link requires access, so the intended audience cannot open it.",
      next: "Create an anyone-with-the-link share set to View.",
    };
  }
  if (id === "m3") {
    const bad = world.apps.find((a) => a.appId === "broad-viewer" && !a.revoked);
    const good = world.apps.find((a) => a.appId === "workshop-helper" && !a.revoked && a.scope === "selected-doc");
    if (bad) {
      return {
        ok: false,
        happened: "The unexpected viewer now has the permissions you approved. No password theft was needed.",
        why: "Mailbox reading and sending mail were not required to open one workshop document.",
        next: "Report it in Help. The simulated response revokes that grant. Changing a password would not do that by itself.",
      };
    }
    if (!good) {
      return {
        ok: false,
        happened: "The approved helper still cannot open the selected workshop document.",
        why: "The trusted guide lists that app for this task, limited to the selected file.",
        next: "Approve the listed helper for the selected document only, and cancel the unexpected viewer.",
      };
    }
    return {
      ok: true,
      happened: world.reported.includes("broad-viewer")
        ? "You cancelled the broad request, reported it, and used the approved helper for the one document."
        : "The approved helper can open the selected workshop document. The unexpected viewer was not approved.",
      why: "A professional consent page is not the same as employer approval. The scope has to match the task.",
      next: "The three required tasks are done. You can practise three more, or return to the terminal.",
    };
  }
  if (id === "m6") {
    const alexSchedule = roleOf(world, "schedule", "alex");
    const alexGuide = roleOf(world, "external", "alex");
    const guest = world.guestIds.includes("alex");
    const flyer = hasAnyoneLink(world, "flyer", "view");
    if (alexSchedule || alexGuide || guest) {
      return {
        ok: false,
        happened: "Alex still has a way into the project files.",
        why: "A link that expired is not the only route. A direct share or guest membership stays until it is removed.",
        next: "Remove Alex's remaining access. Leave the public flyer and the project team.",
      };
    }
    if (!flyer) {
      return {
        ok: false,
        happened: "The public flyer link is gone.",
        why: "Closing the facilitator's access should not remove the flyer the public still needs.",
        next: "Remove only Alex. Leave the flyer link.",
      };
    }
    return {
      ok: true,
      happened: "Alex no longer has project access. The public flyer link still works.",
      why: "A copy Alex already downloaded can remain on their side. Removing access here cannot recall that copy.",
      next: "You can return to the terminal for the quiz.",
    };
  }
  return {
    ok: false,
    happened: "This task is not finished.",
    why: "The share does not match the request.",
    next: "Read the email and try again.",
  };
}
